/**
 * The Dashboard in one sentence or two (16 Oct), at the top of its body: the
 * header carries no subtitle (Jason). Written by rule from the answers the
 * cards already read, so it asks the server for nothing more. The inbox
 * counts and intake totals are whole; the Dashboard's ticket lists stop at
 * `LIST_LIMIT` (apps/api/src/dashboard.ts), so a full list is "or more".
 */
export const LIST_LIMIT = 10;

export type SummaryInput = {
  counts?: { assigned: number; mentioned: number };
  /** How many tickets wait on your reply: the list's length. */
  waitingOnYou?: number;
  review?: { walkthroughs: number; intake: number };
};

const many = (n: number) => (n >= LIST_LIMIT ? `${LIST_LIMIT} or more` : String(n));
const plural = (n: number, said: string, one: string, more = `${one}s`) => `${said} ${n === 1 ? one : more}`;

export function dashboardSummary({ counts, waitingOnYou, review }: SummaryInput): string | null {
  if (!counts || waitingOnYou === undefined) return null;
  const { assigned } = counts;
  const lead = assigned > 0 && waitingOnYou > 0
    ? `${plural(assigned, String(assigned), 'ticket')} ${assigned === 1 ? 'is' : 'are'} assigned to you and ${many(waitingOnYou)} ${waitingOnYou === 1 ? 'is' : 'are'} waiting on your reply.`
    : assigned > 0 ? `${plural(assigned, String(assigned), 'ticket')} ${assigned === 1 ? 'is' : 'are'} assigned to you.`
    : waitingOnYou > 0 ? `${plural(waitingOnYou, many(waitingOnYou), 'ticket')} ${waitingOnYou === 1 ? 'is' : 'are'} waiting on your reply.`
    : 'Nothing needs you right now.';
  const rest = [
    counts.mentioned > 0 ? plural(counts.mentioned, String(counts.mentioned), 'new mention') : null,
    review && review.walkthroughs > 0 ? `${plural(review.walkthroughs, many(review.walkthroughs), 'walkthrough')} to review` : null,
    review && review.intake > 0 ? `${plural(review.intake, String(review.intake), 'report')} in intake` : null,
  ].filter((part): part is string => part !== null);
  if (rest.length === 0) return lead;
  const list = rest.length === 1 ? rest[0]! : `${rest.slice(0, -1).join(', ')} and ${rest[rest.length - 1]}`;
  return `${lead} ${list[0]!.toUpperCase()}${list.slice(1)}.`;
}
